"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { Logos } from "../data.js";
import { useTranslation } from "react-i18next";
import { currentLocale, localeHref } from "@/src/lib/locale-path";

const APP_LINKS = {
  playstore:
    "https://play.google.com/store/apps/details?id=eduversal.eduversal_mobile&hl=id&pli=1",
  iphone: "https://apps.apple.com/id/app/edunav/id1616739654",
  ipad: "https://apps.apple.com/id/app/edunav/id1616739654?platform=ipad",
};

export default function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();
  const locale = currentLocale(usePathname());

  return (
    <footer className="bg-[#F4F9FB]">
      {/* Spacer above banner */}
      <div className="pt-10 sm:pt-14 lg:pt-16" />

      {/* Banner - floats half over the blue section below */}
      <div className="relative z-10 mx-auto w-[80%] max-w-[1500px] -mb-[clamp(2.5rem,10vw,12rem)]">
        <div className="overflow-hidden rounded-3xl shadow-xl">
          <Image
            src={Logos.edunavBanner}
            alt="Edunav - The ultimate and all-in-one School Information System for modern education institutions"
            width={1600}
            height={430}
            className="h-auto w-full object-cover"
          />
        </div>
      </div>

      {/* Footer body */}
      <div className="relative overflow-hidden bg-gradient-to-b from-[#1bacee] to-[#08547e] pt-[clamp(6rem,16vw,20rem)] text-white">
        {/* Decorative pattern: hairline grid fading out toward the top, plus one soft light from the top-right */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [background-image:linear-gradient(to_right,rgba(255,255,255,0.09)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.09)_1px,transparent_1px)] [background-size:64px_64px] [mask-image:linear-gradient(to_top,black,transparent_85%)] [-webkit-mask-image:linear-gradient(to_top,black,transparent_85%)]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 [background-image:radial-gradient(ellipse_60%_50%_at_100%_0%,rgba(255,255,255,0.18),transparent_70%)]"
        />

        <div className="relative z-10 mx-auto flex w-[92%] max-w-[1800px] flex-col gap-10 pb-12 lg:flex-row">
          {/* 35% - Brand */}
          <div className="lg:w-[35%]">
            <div className="relative mb-6 h-[42px] w-[150px]">
              <Image
                src={Logos.edunav}
                alt="Logo Edunav"
                fill
                sizes="150px"
                className="object-contain object-left filter brightness-0 invert"
              />
            </div>
            <p className="max-w-xs text-sm font-semibold leading-relaxed text-white/90" suppressHydrationWarning>
              {t("footer.desc")}
            </p>
          </div>

          {/* 65% - Hubungi Kami / Download App / Legal / Terdaftar di */}
          <div className="grid grid-cols-2 gap-10 sm:grid-cols-4 lg:w-[65%]">
            {/* Hubungi Kami */}
            <div>
              <h4 className="mb-5 text-lg font-bold text-white" suppressHydrationWarning>
                {t("footer.menu1") || "Hubungi Kami"}
              </h4>
              <ul className="space-y-3 text-sm text-white/90">
                <li>
                  <a
                    href="https://api.whatsapp.com/send/?phone=6281370000299&text=Hi%2C+I+want+to+ask+about+zerOne.id+service&type=phone_number&app_absent=0"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    Whatsapp
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.instagram.com/edunav.sis/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    Instagram
                  </a>
                </li>
              </ul>
            </div>

            {/* Download App */}
            <div>
              <h4 className="mb-5 text-lg font-bold text-white" suppressHydrationWarning>
                {t("footer.menu2") || "Tersedia di"}
              </h4>
              <ul className="space-y-3 text-sm text-white/90">
                <li>
                  <a href={APP_LINKS.playstore} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    Playstore
                  </a>
                </li>
                <li>
                  <a href={APP_LINKS.iphone} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    App Store (Iphone)
                  </a>
                </li>
                <li>
                  <a href={APP_LINKS.ipad} target="_blank" rel="noopener noreferrer" className="hover:underline">
                    App Store (Ipad)
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="mb-5 text-lg font-bold text-white" suppressHydrationWarning>
                {t("footer.menu3") || "Informasi Legal"}
              </h4>
              <ul className="space-y-3 text-sm text-white/90">
                <li>
                  <Link href={localeHref("/legal/privacy-policy", locale)} className="hover:underline">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link href={localeHref("/legal/term-of-services", locale)} className="hover:underline">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link href={localeHref("/legal/cookies-policy", locale)} className="hover:underline">
                    Cookies Policy
                  </Link>
                </li>
              </ul>
            </div>

            {/* Terdaftar di */}
            <div>
              <h4 className="mb-5 text-lg font-bold text-white" suppressHydrationWarning>
                {t("footer.menu4") || "Terdaftar di"}
              </h4>
              <Image
                src={Logos.komdigi}
                alt="Logo Kementerian Komunikasi dan Digital Republik Indonesia"
                width={88}
                height={88}
                className="h-20 w-20 object-contain filter brightness-0 invert"
              />
            </div>
          </div>
        </div>

        <div className="relative z-10 mt-12 mx-auto w-[92%] max-w-[1800px] border-t border-white/20 py-8 text-center text-sm font-medium text-white/90">
          Copyright &copy; {currentYear} PT Global Zerone Digital. All Rights Reserved.
        </div>
      </div>
    </footer>
  );
}
